import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Truck,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  Download,
  Repeat,
} from "lucide-react";

const SEED_ORDERS = [
  {
    id: "DT12457890",
    status: "Delivered",
    placed: "12 May 2025",
    delivered: "15 May 2025",
    amount: 1248,
    items: [
      { name: "Multivitamin Fish Oil", qty: 1 },
      { name: "Vitamin C Tablets", qty: 2 },
      { name: "Wellness Kit", qty: 1 },
      { name: "Health Supplement", qty: 1 },
    ],
  },
  {
    id: "DT12453210",
    status: "In Transit",
    placed: "09 May 2025",
    delivered: "17 May 2025",
    amount: 899,
    items: [
      { name: "Face Wash", qty: 2 },
      { name: "Salicylic Acid Serum", qty: 1 },
    ],
  },
  {
    id: "DT12449021",
    status: "Cancelled",
    placed: "04 May 2025",
    delivered: "05 May 2025",
    amount: 549,
    items: [
      { name: "Baby Wipes", qty: 1 },
      { name: "Baby Powder", qty: 1 },
    ],
  },
];

const STATUS_COLOR = {
  Delivered: "bg-emerald-50 text-emerald-700 border-emerald-100",
  "In Transit": "bg-[#F3EFFC] text-[#4B2AAD] border-[#4B2AAD]/20",
  Cancelled: "bg-red-50 text-red-500 border-red-100",
  Processing: "bg-amber-50 text-amber-700 border-amber-100",
};

export default function Orders() {
  const [tab, setTab] = useState("All Orders");
  const [query, setQuery] = useState("");

  const orders = useMemo(() => {
    return SEED_ORDERS.filter((o) => {
      if (tab === "All Orders") return true;
      return o.status === tab;
    }).filter((o) =>
      query.trim() === ""
        ? true
        : (o.id + " " + o.items.map((i) => i.name).join(" "))
            .toLowerCase()
            .includes(query.toLowerCase())
    );
  }, [tab, query]);

  const kpis = useMemo(() => {
    const delivered = SEED_ORDERS.filter((o) => o.status === "Delivered").length;
    const transit = SEED_ORDERS.filter((o) => o.status === "In Transit").length;
    const cancelled = SEED_ORDERS.filter((o) => o.status === "Cancelled").length;
    return { total: SEED_ORDERS.length, delivered, transit, cancelled };
  }, []);

  return (
    <div data-testid="account-orders" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          My Orders
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Track, manage and reorder your purchases.
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={Package} label="Total Orders" value={kpis.total} />
        <Kpi icon={CheckCircle2} label="Delivered" value={kpis.delivered} tone="green" />
        <Kpi icon={Truck} label="In Transit" value={kpis.transit} tone="purple" />
        <Kpi icon={XCircle} label="Cancelled" value={kpis.cancelled} tone="red" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by Order ID, Product or Brand..."
            className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#4B2AAD]"
          />
        </div>
        <button className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-[#4B2AAD]">
          <Filter className="h-3.5 w-3.5" /> Filter
        </button>
        <select
          value={tab}
          onChange={(e) => setTab(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 outline-none focus:border-[#4B2AAD]"
        >
          <option>All Orders</option>
          <option>Delivered</option>
          <option>In Transit</option>
          <option>Cancelled</option>
        </select>
      </div>

      <div className="space-y-3">
        {orders.map((o) => (
          <div
            key={o.id}
            data-testid={`order-card-${o.id}`}
            className="rounded-2xl border border-gray-100 bg-white p-5"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-[#4B2AAD]">
                    Order ID <span className="text-gray-900">#{o.id}</span>
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold border ${STATUS_COLOR[o.status]}`}
                  >
                    {o.status}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Order Date <span className="font-semibold text-gray-700">{o.placed}</span>
                </p>
                <p className="text-xs text-gray-500">
                  Total Amount <span className="font-semibold text-gray-900">₹{o.amount.toLocaleString()}</span>
                </p>
                <div className="mt-3 flex items-center gap-2">
                  {o.items.slice(0, 4).map((it, i) => (
                    <div
                      key={i}
                      className="h-10 w-10 rounded-lg bg-[#F5F3FB] flex items-center justify-center border border-gray-100 text-[10px] text-gray-500"
                    >
                      Item
                    </div>
                  ))}
                  {o.items.length > 4 && (
                    <div className="h-10 w-10 rounded-lg bg-[#F3EFFC] flex items-center justify-center text-xs font-bold text-[#4B2AAD]">
                      +{o.items.length - 4}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 items-end min-w-[180px]">
                <p className="text-xs text-gray-500">
                  {o.status === "Delivered"
                    ? "Delivered on"
                    : o.status === "Cancelled"
                      ? "Cancelled on"
                      : "Expected Delivery"}
                </p>
                <p className="text-sm font-bold text-gray-900">{o.delivered}</p>
                <div className="flex flex-col w-full gap-2 mt-2">
                  {o.status === "Delivered" || o.status === "In Transit" ? (
                    <button className="dyntra-btn-primary text-xs py-1.5">Track Order</button>
                  ) : null}
                  <button className="dyntra-btn-ghost text-xs py-1.5">View Details</button>
                  <button className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#4B2AAD] hover:underline">
                    <Download className="h-3.5 w-3.5" /> Download Invoice
                  </button>
                  <button className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#4B2AAD] hover:underline">
                    <Repeat className="h-3.5 w-3.5" /> Reorder
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {orders.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
            <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No orders match your filters.</p>
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-4 text-xs text-gray-700 flex items-center justify-between gap-3 flex-wrap">
        <span>Need help with your order? Our support team is here for you.</span>
        <Link to="/account/help" className="dyntra-btn-ghost text-xs py-1.5 px-3">
          Contact Support
        </Link>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const iconBg =
    tone === "green"
      ? "bg-emerald-50 text-emerald-600"
      : tone === "red"
        ? "bg-red-50 text-red-500"
        : "bg-[#F3EFFC] text-[#4B2AAD]";
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconBg}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
        <p className="text-[11px] text-gray-500">{label}</p>
      </div>
    </div>
  );
}
